// pages/marketing/MinhasConversas/index.tsx
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Card, CardBody, Row, Col, Spinner } from "reactstrap";
import moment from "moment";
import "moment/locale/pt-br";
import api from "../../../services/api";
import EmpresaContext from "../../../contexts/Empresa";
import { normalizePhone } from "../../../utils";
import axios from "axios";
import "./styles.css";

type Usuario = {
  id: number;
  name: string;
};

type Conversa = {
  id: string | number;
  titulo?: string;
  contato_nome?: string;
  contato_avatar?: string;
  ultimo_texto?: string;
  ultima_data?: string;
  contactPhone?: string;
  sellerPhone?: string;
};

type Mensagem = {
  id: string | number;
  texto: string;
  criado_em: string; // ISO
  direcao: "in" | "out";
};

const customStyles = {
  container: (provided: any) => ({ ...provided, width: "100%" }),
  option: (p: any) => ({ ...p, fontSize: 13, zIndex: 100 }),
  menu: (p: any) => ({ ...p, zIndex: 100 }),
  control: (p: any) => ({ ...p, minHeight: 38, fontSize: 13 }),
};

const labelMessage = (m: any) => {
  if (!m) return "—";
  if (m.text) return m.text;
  const t = (m.type || "").toLowerCase();
  if (t.includes("image")) return "[Foto]";
  if (t.includes("video")) return "[Vídeo]";
  if (t.includes("sticker")) return "[Figurinha]";
  return "[Mensagem]";
};

const pickTime = (m: any) =>
  m?.timestampOriginal ?? m?.createdAt ?? m?.criado_em;

export default function MinhasConversas() {
  const { empresaSelecionada } = React.useContext(EmpresaContext);
  const empresaId = empresaSelecionada?.id;

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);

  const [vendedor, setVendedor] = useState<Usuario | null>(null);

  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [loadingConversas, setLoadingConversas] = useState(false);
  const [conversaSelecionada, setConversaSelecionada] =
    useState<Conversa | null>(null);

  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [loadingMensagens, setLoadingMensagens] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);

  async function findDedicatedBaileysIntegration() {
    const response = await api.get(
      `esb/integracoes/ativas?types[]=whatsapp&empresaId=${empresaSelecionada?.id}`
    );

    let dedicatedUrl = null;
    if (response.data[0]) {
      dedicatedUrl = response.data[0].valor;
    }

    return dedicatedUrl ? dedicatedUrl : process.env.REACT_APP_API_BAILEYS_URL;
  }

  useEffect(() => {
    moment.locale("pt-br");
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    const fetchUsuarios = async () => {
      try {
        setLoadingUsuarios(true);

        const { data } = await api.get(
          `/common/empresas/${empresaId}/comercial`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("@playnee-token")}`,
            },
          }
        );

        const list: Usuario[] = (
          Array.isArray(data) ? data : data?.data || []
        ).map((u: any) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          avatar_url: u.avatar_url,
        }));

        setUsuarios(list);
        setVendedor(null);
        setConversas([]);
        setConversaSelecionada(null);
        setMensagens([]);
      } catch (err) {
        console.error("Erro ao carregar usuários (comercial):", err);
      } finally {
        setLoadingUsuarios(false);
      }
    };
    fetchUsuarios();
  }, [empresaId]);

  useEffect(() => {
    if (!empresaId || !vendedor?.id) return;
    setLoadingConversas(true);
    setConversaSelecionada(null);
    setMensagens([]);

    (async () => {
      try {
        // 1) Buscar número do vendedor no canal 25
        const { data: canal } = await api.get(
          `/marketing/canal-contato/${vendedor.id}/25`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("@playnee-token")}`,
            },
          }
        );

        const sellerPhone = canal?.identificador?.replace(/\D+/g, "");
        if (!sellerPhone) {
          setConversas([]);
          return;
        }

        const phoneTreated = normalizePhone(sellerPhone);

        // 2) Buscar conversas pelo número do vendedor
        const API_BAILEYS_URL = await findDedicatedBaileysIntegration();

        const { data } = await axios.get(
          `${API_BAILEYS_URL}/conversations/empresas/${empresaId}/${phoneTreated}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("@playnee-token")}`,
            },
          }
        );

        // mapa base das conversas
        const baseList: Conversa[] = (data?.conversations || []).map(
          (c: any) => ({
            id: c.id,
            titulo: c.contatoNome || c.phoneNumberContacts,
            contato_nome: c.contatoNome || c.phoneNumberContacts,
            contato_avatar: "",
            ultimo_texto: "", // vamos preencher já já
            ultima_data: c.updatedAt || c.createdAt,
            contactPhone: c.phoneNumberContacts,
            sellerPhone: phoneTreated,
          })
        );

        // 3) Para cada conversa, busque somente a última mensagem (pageSize=1)
        const enriched = await Promise.all(
          baseList.map(async (conv) => {
            try {
              const { data: mres } = await axios.get(
                `${API_BAILEYS_URL}/empresas/${empresaId}/accounts/${phoneTreated}/contacts/${conv.contactPhone}/messages`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "@playnee-token"
                    )}`,
                  },
                }
              );
              const last = mres?.messages?.[mres.messages.length - 1];
              return {
                ...conv,
                ultimo_texto: labelMessage(last) || conv.ultimo_texto,
                ultima_data: pickTime(last) || conv.ultima_data,
              };
            } catch {
              return conv; // se falhar, mantém o básico
            }
          })
        );

        // ordena por última atividade desc
        enriched.sort((a, b) => {
          const ta = new Date(a.ultima_data || 0).getTime();
          const tb = new Date(b.ultima_data || 0).getTime();
          return tb - ta;
        });

        setConversas(enriched);
      } catch (err) {
        console.error("Erro ao carregar conversas:", err);
        setConversas([]);
      } finally {
        setLoadingConversas(false);
      }
    })();
  }, [empresaId, vendedor?.id]);

  useEffect(() => {
    fetchAccounts();
  }, [empresaId]);

  async function fetchAccounts() {
    try {
      const { data } = await api.get(`/meta/all-accounts/empresa/${empresaId}`);

      setAccounts(data || []);
    } catch (err) {
      console.error("Erro ao carregar contas Meta:", err);
    }
  }

  useEffect(() => {
    if (!empresaId || !conversaSelecionada?.id) return;
    setLoadingMensagens(true);

    (async () => {
      try {
        const seller = normalizePhone(conversaSelecionada.sellerPhone || "");
        const contact = normalizePhone(conversaSelecionada.contactPhone || "");
        if (!seller || !contact) {
          setMensagens([]);
          return;
        }

        const API_BAILEYS_URL = await findDedicatedBaileysIntegration();

        const { data } = await axios.get(
          `${API_BAILEYS_URL}/empresas/${empresaId}/accounts/${seller}/contacts/${contact}/messages`,
          {
            // se quiser paginação infinita depois, passe page/pageSize aqui
          }
        );

        const arr = Array.isArray(data?.messages) ? data.messages : [];

        // ordena pelo tempo real da msg
        arr.sort((a: any, b: any) => {
          const ta = new Date(pickTime(a) || 0).getTime();
          const tb = new Date(pickTime(b) || 0).getTime();
          return ta - tb; // ascendente
        });

        const msgs: Mensagem[] = arr.map((m: any) => ({
          id: m.id,
          texto: labelMessage(m),
          criado_em: pickTime(m),
          direcao: m.fromMe ? "out" : "in",
        }));

        setMensagens(msgs);
      } catch (err) {
        console.error("Erro ao carregar mensagens:", err);
        setMensagens([]);
      } finally {
        setLoadingMensagens(false);
      }
    })();
  }, [empresaId, conversaSelecionada?.id]);

  const optionsUsuarios = useMemo(
    () =>
      usuarios.map((u) => ({
        value: u.id,
        label: u.name,
        whatsapp: accounts.some((acc) => acc.user_id === u.id) || false,
      })),
    [usuarios]
  );

  return (
    <Card>
      <CardBody>
        <Row style={{ height: "72vh" }}>
          <Col
            md={3}
            className="border-right d-flex flex-column"
            style={{ height: "100%" }}
          >
            <div style={{ marginBottom: 8 }}>
              <Select
                isLoading={loadingUsuarios}
                placeholder={
                  loadingUsuarios
                    ? "Carregando usuários..."
                    : "Selecione o vendedor"
                }
                styles={customStyles}
                options={optionsUsuarios}
                value={
                  vendedor ? { value: vendedor.id, label: vendedor.name } : null
                }
                formatOptionLabel={(option: any) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        lineHeight: 1.1,
                        gap: 6,
                      }}
                    >
                      <span>{option.label} </span>
                      <span>
                        {option.whatsapp ? (
                          <i className="fab fa-whatsapp text-success text-md"></i>
                        ) : (
                          ""
                        )}{" "}
                      </span>
                    </div>
                  );
                }}
                onChange={(opt: any) => {
                  const u = usuarios.find((x) => x.id === opt?.value) || null;
                  setVendedor(u);
                }}
                noOptionsMessage={() =>
                  loadingUsuarios
                    ? "Carregando..."
                    : "Nenhum usuário encontrado"
                }
              />
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              <div className="pb-2 font-weight-bold">Conversas</div>

              {loadingConversas && (
                <div className="d-flex align-items-center text-muted">
                  <Spinner size="sm" className="mr-2" /> Carregando conversas...
                </div>
              )}

              {!loadingConversas && conversas.length === 0 && vendedor && (
                <div className="text-muted">
                  Nenhuma conversa encontrada para {vendedor.name}.
                </div>
              )}

              <ul className="list-group list-group-flush">
                {conversas.map((c) => (
                  <li
                    key={c.id}
                    className={`list-group-item ${
                      conversaSelecionada?.id === c.id ? "activenewchat" : ""
                    }`}
                    onClick={() => setConversaSelecionada(c)}
                    style={{ cursor: "pointer" }}
                  >
                    {/* Linha superior: nome à esquerda e tempo à direita */}
                    <div className="d-flex align-items-center justify-content-between">
                      <strong className="text-dark">
                        {c.contato_nome || c.titulo || "Sem nome"}
                      </strong>

                      <small className="text-muted">
                        {c.ultima_data ? moment(c.ultima_data).fromNow() : ""}
                      </small>
                    </div>

                    {/* Última mensagem, truncada em uma linha */}
                    <small className="text-muted text-truncate d-block">
                      {c.ultimo_texto || "—"}
                    </small>
                  </li>
                ))}
              </ul>
            </div>
          </Col>

          <Col md={9} style={{ height: "100%", overflowY: "auto" }}>
            {conversaSelecionada ? (
              <>
                <div className="pb-2 border-bottom d-flex align-items-center">
                  <div className="d-flex flex-column">
                    <strong>
                      {conversaSelecionada.contato_nome ||
                        conversaSelecionada.titulo ||
                        "Conversa"}
                    </strong>
                    <small className="text-muted">
                      {conversaSelecionada.ultima_data
                        ? `Última atividade ${moment(
                            conversaSelecionada.ultima_data
                          ).fromNow()}`
                        : ""}
                    </small>
                  </div>
                </div>

                {loadingMensagens ? (
                  <div className="d-flex align-items-center text-muted mt-3">
                    <Spinner size="sm" className="mr-2" /> Carregando
                    mensagens...
                  </div>
                ) : mensagens.length === 0 ? (
                  <div className="text-muted mt-3">
                    Nenhuma mensagem nessa conversa.
                  </div>
                ) : (
                  <div className="chat-wrap">
                    {mensagens.map((m, i) => {
                      const currentDate = moment(m.criado_em).format(
                        "DD/MM/YYYY"
                      );
                      const prevDate =
                        i > 0
                          ? moment(mensagens[i - 1].criado_em).format(
                              "DD/MM/YYYY"
                            )
                          : null;

                      const showDivider = currentDate !== prevDate;
                      const isOut = m.direcao === "out";

                      return (
                        <div key={m.id}>
                          {showDivider && (
                            <div className="chat-date-divider">
                              <span>{currentDate}</span>
                            </div>
                          )}

                          <div
                            className={`msg-row ${
                              isOut ? "msg-row--out" : "msg-row--in"
                            }`}
                          >
                            <div
                              className={`msg ${
                                isOut ? "msg--out" : "msg--in"
                              }`}
                            >
                              <div>{m.texto}</div>
                              <small className="msg-time">
                                {moment(m.criado_em).format("HH:mm")}
                              </small>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : vendedor ? (
              <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                Selecione uma conversa à esquerda
              </div>
            ) : (
              <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                Selecione um vendedor acima para carregar as conversas
              </div>
            )}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}
